import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableRow,
  useTheme,
  Box,
} from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import {
  deleteTrainingCourse,
  getTrainingCourses,
} from "../../../reducers/TrainingCourseReducer";
import PATEditButton from "../../../components/Buttons/PATEditButton";
import PATDeleteButton from "../../../components/Buttons/PATDeleteButton";
import { updateOrCreateUserSetting } from "../../../reducers/UserSettingsReducer";
import { AMSModules, paginationStyle, serviceNames } from "../../../utils/constants";
import DeleteConfirmationDialog from "../../../components/DeleteConfirmationDialog";
import {
  formatDateToMonthDateYear,
  getRowsPerPageOptions,
  mapDictionaryOptions,
} from "../../../utils/functions";
import CourseTableToolbar from "./CourseTableToolbar";
import { getService } from "../../../reducers/service";
import {
  PATH_TRAINING_COURSES_ADD,
  PATH_TRAINING_COURSES_EDIT,
} from "../../../utils/routeConstants";
import { getAccessPermission } from "../../../reducers/PermissionsReducer";
import PATViewButton from "../../../components/Buttons/PATViewButton";

const INITIAL_PAGE = 0;
const INITIAL_PAGE_SIZE = 20;
const INITIAL_SORT_MODEL = [{ field: "id", sort: "asc" }];

export const initialFilterState = {
  courseName: "",
  location: "",
  instructor: "",
  category: "",
  hours: "",
  courseNumber: "",
  exactMatch: false,
  tagTypes: [],
};

const Courses = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const history = useHistory();

  const { trainingCourses, loading, total } = useSelector(
    (state) => state.trainingCourses
  );
  const { userSettings } = useSelector((state) => state.userSettings);
  const themeMode = useSelector((state) => state.theme.mode);
  const { userData, isAuthenticated } = useSelector((state) => state.user);
  const userAgency = useSelector((state) => state.user.userAgency);
  const filtersInfo = useSelector((state) => state.filtersInfo);

  const [page, setPage] = useState(INITIAL_PAGE);
  const [pageSize, setPageSize] = useState(INITIAL_PAGE_SIZE);
  const [sortModel, setSortModel] = useState(INITIAL_SORT_MODEL);
  const [selectedId, setSelectedId] = useState(null);
  const [columnsOrder, setColumnsOrder] = useState([]);
  const [columnsSize, setColumnsSize] = useState([]);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [filters, setFilters] = useState(filtersInfo.courses || initialFilterState);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [locationOptions, setLocationOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const currentAgency = useRef(null);
  const dictionary = useSelector((state) => state.dictionary.dictionary);
  const tagTypeOptions = mapDictionaryOptions(dictionary.codePerTagTypes);

  const coursePermission = getAccessPermission("training", "Courses", "any");
  const canReadCourse = coursePermission?.Read ?? true;
  const canEditCourse = coursePermission?.Edit ?? true;
  const canDeleteCourse = coursePermission?.Delete ?? true;

  const fetchDropdownValues = async () => {
    const services = {
      distinct: getService(serviceNames.trainingCourseDistinct),
    };

    const results = await Promise.all([
      services.distinct.find({
        query: { column: ["location", "category"], agencyId: userAgency },
      }),
    ]);

    setLocationOptions(
      results[0]?.location.map((value) => ({ label: value, value })) || []
    );
    setCategoryOptions(
      results[0]?.category.map((value) => ({ label: value, value })) || []
    );
  };

  useEffect(() => {
    if (!settingsLoaded || !isAuthenticated) return;
    const { field, sort } = sortModel[0] || {};
    if (field && sort) {
      const isAgencyChanged = currentAgency.current !== userAgency;
			if (isAgencyChanged) {
				resetFilters();
				currentAgency.current = userAgency;
			}
      dispatch(
        getTrainingCourses(page, pageSize, field, sort, isAgencyChanged ? initialFilterState : filters, userAgency)
      );
    }
  }, [
    dispatch,
    page,
    pageSize,
    sortModel,
    filters,
    settingsLoaded,
    isAuthenticated,
    userAgency,
  ]);

  const baseColumns = useMemo(
    () => [
      {
        field: "courseNumber",
        headerName: "Course #",
        width: 150,
        sortable: true,
      },
      {
        field: "courseName",
        headerName: "Course Name",
        width: 150,
        sortable: true,
      },
      {
        field: "courseBeginDate",
        headerName: "Begin Date",
        width: 150,
        sortable: true,
        valueFormatter: (params) => {
          return formatDateToMonthDateYear(params.value);
        },
      },
      {
        field: "courseEndDate",
        headerName: "End Date",
        width: 150,
        sortable: true,
        valueFormatter: (params) => {
          return formatDateToMonthDateYear(params.value);
        },
      },
      {
        field: "location",
        headerName: "Location",
        width: 100,
        sortable: true,
      },
      {
        field: "category",
        headerName: "Category",
        width: 100,
        sortable: false,
      },
      {
        field: "instructor",
        headerName: "Instructor",
        width: 100,
      },
      {
        field: "actions",
        headerName: "Actions",
        type: "actions",
        width: 120,
        align: "center",
        sortable: false,
        renderCell: ({ row }) =>
          row.id ? (
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell align="right" sx={{ padding: 0.5 }}>
                    {canEditCourse && (
                      <PATEditButton
                        tooltipText="Edit Training Course"
                        onClick={() =>
                          history.push(`${PATH_TRAINING_COURSES_EDIT}${row.id}`)
                        }
                        size="20px"
                      />
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ padding: 0.5 }}>
                    {canReadCourse && !canEditCourse && (
                      <PATViewButton
                        tooltipText="View Course Details"
                        onClick={() =>
                          history.push({
                            pathname: `${PATH_TRAINING_COURSES_EDIT}${row.id}`,
                            search: "?readOnly=true",
                          })
                        }
                        size="20px"
                      />
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ padding: 0.5 }}>
                    {canDeleteCourse && (
                      <PATDeleteButton
                        tooltipText="Delete Training Course"
                        onClick={() => {
                          setSelectedId(row.id);
                          setOpenDeleteDialog(true);
                        }}
                        size="20px"
                      />
                    )}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          ) : null,
      },
    ],
    [history, canDeleteCourse, canEditCourse, canReadCourse]
  );

  const columns = useMemo(() => {
    const hasOrder = columnsOrder.length > 0;
    const hasSizes = Object.keys(columnsSize).length > 0;

    if (!hasOrder && !hasSizes) return baseColumns;

    let baseOrderedColumns;

    if (hasOrder) {
      const ordered = columnsOrder
        .map((field) => baseColumns.find((col) => col.field === field))
        .filter(Boolean);

      const remaining = baseColumns.filter(
        (col) => !columnsOrder.includes(col.field)
      );

      const actionsIndex = ordered.findIndex((col) => col.field === "actions");
      const insertAt = actionsIndex > -1 ? actionsIndex : ordered.length;

      baseOrderedColumns = [
        ...ordered.slice(0, insertAt),
        ...remaining,
        ...ordered.slice(insertAt),
      ];
    } else {
      baseOrderedColumns = baseColumns;
    }

    const finalColumns = baseOrderedColumns.map((col) => ({
      ...col,
      width: columnsSize[col.field] ?? col.width ?? 100,
    }));

    return finalColumns;
  }, [baseColumns, columnsOrder, columnsSize]);

  const allColumnFields = baseColumns.map((col) => col.field);
  useEffect(() => {
    const defaultVisibility = {};
    currentAgency.current = userAgency;
    allColumnFields.forEach((field) => {
      defaultVisibility[field] = true;
    });
    setColumnVisibilityModel((prev) => ({ ...defaultVisibility, ...prev }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isAuthenticated || settingsLoaded) return;

    const initialize = async () => {
      await fetchDropdownValues();
      const settingsMap = Object.fromEntries(
        userSettings.map((s) => [s.key, s.value])
      );
      if (settingsMap["trainingCourseTableColumnOrder"]) {
        setColumnsOrder(
          JSON.parse(settingsMap["trainingCourseTableColumnOrder"])
        );
      }

      if (settingsMap["trainingCourseTableColumnSizes"]) {
        const widthArray = JSON.parse(
          settingsMap["trainingCourseTableColumnSizes"]
        );
        const result = widthArray.reduce((acc, curr) => {
          acc[curr.field] = curr.width;
          return acc;
        }, {});
        setColumnsSize(result);
      }

      if (settingsMap["trainingCourseTableColumnVisibility"]) {
        setColumnVisibilityModel(
          JSON.parse(settingsMap["trainingCourseTableColumnVisibility"])
        );
      }

      if (settingsMap["trainingCourseTableSortModel"]) {
        const savedSortModel = JSON.parse(
          settingsMap["trainingCourseTableSortModel"]
        );
        if (Array.isArray(savedSortModel)) {
          const validSortModel = savedSortModel.filter((model) =>
            baseColumns.some((col) => col.field === model.field)
          );
          if (validSortModel.length > 0) {
            setSortModel(savedSortModel);
          }
        }
      }
      setSettingsLoaded(true);
    };

    initialize();
  }, [isAuthenticated, userSettings, settingsLoaded, baseColumns]);

  useEffect(() => {
    if (!isAuthenticated || !userAgency) return;
    fetchDropdownValues();
  }, [userAgency, isAuthenticated]);

  const handleSortModelChange = useCallback(
    (model) => {
      const newSortModel = model.length > 0 ? model : INITIAL_SORT_MODEL;

      // Avoid unnecessary state updates if sort model didn't change
      const isSortModelDifferent =
        JSON.stringify(newSortModel) !== JSON.stringify(sortModel);

      if (isSortModelDifferent) {
        setSortModel(newSortModel);
        setPage(INITIAL_PAGE);

        if (userData?.user?.ptsUserID) {
          dispatch(
            updateOrCreateUserSetting(
              userData.user.ptsUserID,
              "trainingCourseTableSortModel",
              JSON.stringify(newSortModel)
            )
          );
        }
      }
    },
    [sortModel, dispatch, userData?.user?.ptsUserID]
  );

  const handleColumnOrderChange = useCallback(
    (params, event, details) => {
      const newColumnOrder = details.api
        .getAllColumns()
        .map((col) => col.field);
      if (userData?.user?.ptsUserID) {
        dispatch(
          updateOrCreateUserSetting(
            userData.user.ptsUserID,
            "trainingCourseTableColumnOrder",
            JSON.stringify(newColumnOrder)
          )
        );
      }
    },
    [dispatch, userData?.user?.ptsUserID]
  );

  const handleColumnWidthChange = (params, event, details) => {
    const columnSizes = details.api.getAllColumns().map((col) => {
      return {
        field: col.field,
        width: col.width,
      };
    });
    if (userData?.user?.ptsUserID) {
      dispatch(
        updateOrCreateUserSetting(
          userData.user.ptsUserID,
          "trainingCourseTableColumnSizes",
          JSON.stringify(columnSizes)
        )
      );
    }
  };

  const handleColumnVisibilityModelChange = useCallback(
    (newModel) => {
      setColumnVisibilityModel(newModel);
      if (userData?.user?.ptsUserID) {
        dispatch(
          updateOrCreateUserSetting(
            userData.user.ptsUserID,
            "trainingCourseTableColumnVisibility",
            JSON.stringify(newModel)
          )
        );
      }
    },
    [dispatch, userData?.user?.ptsUserID]
  );

  const handleDelete = useCallback(() => {
    if (selectedId) {
      dispatch(deleteTrainingCourse(selectedId, userData?.user?.ptsUserID));
      setOpenDeleteDialog(false);
      setSelectedId(null);
      fetchDropdownValues();
    }
  }, [dispatch, selectedId, userData?.user?.ptsUserID]);

  const resetFilters = () => {
    setFilters(initialFilterState);
    dispatch({ type: AMSModules.Courses, payload: initialFilterState });
  };

  const handleOnFilterChange = (newFilters) => {
    setFilters(newFilters);
    dispatch({ type: AMSModules.Courses, payload: newFilters });
  }

  const rowsPerPageOptions = useMemo(
    () => getRowsPerPageOptions(total),
    [total]
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 85px)", // or any other dynamic value subtracting top nav height
        marginTop: theme.spacing(0.5),
      }}
    >
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          overflow: "hidden",
          m: 1,
          p: 2,
        }}
      >
        {canReadCourse ? (
          <></>
        ) : (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <h3>You don't have permission to view courses.</h3>
          </Box>
        )}
        <CourseTableToolbar
          onAddClick={() => history.push(`${PATH_TRAINING_COURSES_ADD}`)}
          courses={trainingCourses}
          onFilterChange={handleOnFilterChange}
          filters={filters}
          resetFilters={resetFilters}
          locationOptions={locationOptions}
          categoryOptions={categoryOptions}
          tagTypeOptions={tagTypeOptions}
        />

        <Box
          sx={{
            flex: 1,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <DataGridPro
            rows={trainingCourses}
            columns={columns}
            initialState={{ pinnedColumns: { right: ["actions"] } }}
            getRowId={(row) => row.GUID}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
            pageSize={pageSize}
            rowsPerPageOptions={rowsPerPageOptions}
            pagination
            paginationMode="server"
            sortModel={sortModel}
            sortingMode="server"
            onSortModelChange={handleSortModelChange}
            rowCount={total}
            page={page}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            loading={loading}
            disableSelectionOnClick
            onColumnOrderChange={handleColumnOrderChange}
            onColumnWidthChange={handleColumnWidthChange}
            sx={{
              ...paginationStyle(theme, themeMode),
              color: theme.palette.colorFont,
              margin: 1,
            }}
          />
        </Box>

        <DeleteConfirmationDialog
          open={openDeleteDialog}
          onClose={() => {
            setOpenDeleteDialog(false);
            setSelectedId(null);
          }}
          onConfirm={handleDelete}
          title="Delete Training Course"
          message="Are you sure you want to delete this training course?"
        />
      </Paper>
    </Box>
  );
};

export default Courses;
